import { useEffect, useRef, useState } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';

export const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export const stagger = (delayChildren = 0.08, staggerChildren = 0.08) => ({
  hidden: {},
  visible: { transition: { delayChildren, staggerChildren } },
});

export function Reveal({ children, className = '', delay = 0, y = 32, once = true, testId }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once, margin: '-80px 0px' });
  const reduce = useReducedMotion();

  if (reduce) {
    return <div ref={ref} className={className} data-testid={testId}>{children}</div>;
  }

  return (
    <motion.div
      ref={ref}
      className={className}
      data-testid={testId}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay }}
    >
      {children}
    </motion.div>
  );
}

export function StaggerContainer({ children, className = '', testId }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px 0px' });
  return (
    <motion.div
      ref={ref}
      className={className}
      data-testid={testId}
      variants={stagger(0.1, 0.08)}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({ children, className = '' }) {
  return (
    <motion.div variants={fadeUp} className={className}>
      {children}
    </motion.div>
  );
}

// Word-by-word reveal for hero headline
export function SplitReveal({ text, className = '', delay = 0.05, testId }) {
  const reduce = useReducedMotion();
  if (reduce) return <span className={className} data-testid={testId}>{text}</span>;
  const words = text.split(' ');
  return (
    <span className={className} data-testid={testId}>
      {words.map((w, i) => (
        <span key={i} className="inline-block overflow-hidden align-baseline">
          <motion.span
            className="inline-block"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: delay + i * 0.06 }}
          >
            {w}{i < words.length - 1 ? '\u00A0' : ''}
          </motion.span>
        </span>
      ))}
    </span>
  );
}
